import DOMPurify, { type Config } from 'dompurify';

/**
 * The answer HTML allowlist of spec §5 (and chat-runs-api.md §9). Run on
 * every render and before every copy: the server does not sanitize, and the
 * model can return anything.
 *
 * - Allowed: h3, h4, p, strong/b, em/i, ul/ol/li, table/thead/tbody/tr/th/td
 *   (colspan, rowspan), blockquote, pre, code, br, a (http, https, mailto).
 * - Removed with their content: script, style, iframe, form and its
 *   controls, SVG, MathML, embedded objects and media.
 * - Other HTML elements (div, span, h1…) are unwrapped: their text stays.
 * - Elements HTML does not know (`<userid>`) are shown as text.
 * - Every attribute except href, colspan and rowspan is dropped (event
 *   handlers, style, class, id). Links open in a new tab with
 *   rel="noopener noreferrer"; any other URL scheme, javascript: and data:
 *   included, loses its href.
 */

export const ALLOWED_TAGS = [
  'h3',
  'h4',
  'p',
  'strong',
  'b',
  'em',
  'i',
  'ul',
  'ol',
  'li',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'blockquote',
  'pre',
  'code',
  'br',
  'a',
];

const ALLOWED_ATTR = ['href', 'colspan', 'rowspan'];

/** Dropped together with everything inside them. */
const FORBID_WITH_CONTENT = [
  'script',
  'style',
  'iframe',
  'frame',
  'frameset',
  'object',
  'embed',
  'applet',
  'form',
  'button',
  'input',
  'select',
  'option',
  'textarea',
  'svg',
  'math',
  'template',
  'noscript',
  'noembed',
  'noframes',
  'audio',
  'video',
  'canvas',
  'img',
  'picture',
  'source',
  'link',
  'meta',
  'base',
  'title',
  'head',
  'xmp',
  'plaintext',
];

const SAFE_URL = /^(?:https?:|mailto:)/i;

const CONFIG: Config = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  ALLOWED_URI_REGEXP: SAFE_URL,
  // Not URLs: without this DOMPurify checks their values against the regexp.
  ADD_URI_SAFE_ATTR: ['colspan', 'rowspan'],
  FORBID_CONTENTS: FORBID_WITH_CONTENT,
  KEEP_CONTENT: true,
  WHOLE_DOCUMENT: false,
};

const isUnknownElement = (node: Node): node is Element => {
  if (node.nodeType !== 1) return false;
  const element = node as Element;
  const name = element.localName;
  // Custom-element names (with a hyphen) are HTMLElement, not unknown.
  return (
    name.includes('-') ||
    Object.prototype.toString.call(element) === '[object HTMLUnknownElement]'
  );
};

let hooked = false;

const installHooks = () => {
  if (hooked) return;
  hooked = true;

  // Unknown elements become text: "<name>" … "</name>" around their content
  // (KEEP_CONTENT moves the content between the two).
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.allowedTags[data.tagName]) return;
    if (FORBID_WITH_CONTENT.includes(data.tagName)) return;
    if (!isUnknownElement(node)) return;
    const parent = node.parentNode;
    if (!parent) return;
    const document = node.ownerDocument;
    parent.insertBefore(document.createTextNode(`<${data.tagName}>`), node);
    parent.insertBefore(
      document.createTextNode(`</${data.tagName}>`),
      node.nextSibling,
    );
  });

  DOMPurify.addHook('afterSanitizeAttributes', node => {
    for (const span of ['colspan', 'rowspan']) {
      const value = node.getAttribute(span);
      if (value !== null && !/^\s*[1-9]\d{0,2}\s*$/.test(value)) {
        node.removeAttribute(span);
      }
    }
    if (node.nodeName !== 'A') return;
    const href = node.getAttribute('href');
    if (href && SAFE_URL.test(href.trim())) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    } else {
      node.removeAttribute('href');
    }
  });
};

/** The answer HTML, sanitized, as a string. */
export const sanitizeAnswerHtml = (html: string): string => {
  installHooks();
  return DOMPurify.sanitize(html, {
    ...CONFIG,
    RETURN_TRUSTED_TYPE: false,
  }) as string;
};

/** The answer HTML, sanitized, as a fragment to post-process. */
export const sanitizeToFragment = (html: string): DocumentFragment => {
  installHooks();
  return DOMPurify.sanitize(html, {
    ...CONFIG,
    RETURN_DOM_FRAGMENT: true,
  }) as unknown as DocumentFragment;
};
